import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StaffCard } from "@/components/schedora/StaffCard";
import { useApp, useOwnerSnapshot } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const blankForm = {
  email: "",
  name: "",
  role: "",
  commissionRate: "40",
  availability: "",
  specialties: "",
  pin: "",
};

export default function OwnerStaff() {
  const snapshot = useOwnerSnapshot(new Date().toISOString().slice(0, 10));
  const { addStaff, updateStaff, deleteStaff } = useApp();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(blankForm);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!editingId) {
      setForm(blankForm);
      return;
    }

    const person = snapshot?.staff.find((entry) => entry.id === editingId);
    if (person) {
      setForm({
        email: person.email,
        name: person.name,
        role: person.role,
        commissionRate: String(person.commissionRate),
        availability: person.availability,
        specialties: person.specialties.join(", "),
        pin: "",
      });
    }
  }, [editingId, snapshot?.staff]);

  const payload = {
    email: form.email,
    name: form.name,
    role: form.role,
    commissionRate: Number(form.commissionRate),
    availability: form.availability,
    specialties: form.specialties.split(",").map((entry) => entry.trim()).filter(Boolean),
    pin: form.pin.trim() || undefined,
  };

  const submit = async () => {
    setError("");
    setMessage("");
    if (!payload.name.trim() || !payload.email.trim() || !payload.role.trim()) {
      setError("Name, email, and role are required.");
      return;
    }
    if (!payload.email.includes("@")) {
      setError("Enter a valid staff email.");
      return;
    }
    if (payload.specialties.length === 0) {
      setError("Add at least one specialty.");
      return;
    }
    if (!Number.isFinite(payload.commissionRate) || payload.commissionRate < 0 || payload.commissionRate > 100) {
      setError("Commission rate must be between 0 and 100.");
      return;
    }
    if (!editingId && !payload.pin) {
      setError("Add a 4-digit staff PIN.");
      return;
    }
    if (payload.pin && !/^\d{4}$/.test(payload.pin)) {
      setError("PIN must be exactly 4 digits.");
      return;
    }
    setIsSaving(true);
    try {
      if (editingId) {
        await updateStaff(editingId, payload);
        setMessage("Team member updated.");
      } else {
        await addStaff(payload);
        setMessage("Team member added and invitation sent.");
      }
    } catch (nextError: any) {
      setError(nextError?.message ?? "Unable to save team member.");
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    setOpen(false);
    setEditingId(null);
    setForm(blankForm);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Staff</h1>
          <p className="text-sm text-muted-foreground">{snapshot?.staff.length ?? 0} team members</p>
        </div>
        <Dialog open={open} onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setEditingId(null);
            setForm(blankForm);
          }
        }}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="mr-1 h-4 w-4" /> Add Staff</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Team Member" : "Add Team Member"}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div><Label>Full Name</Label><Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className="mt-1" /></div>
              <div><Label>Role</Label><Input value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} className="mt-1" /></div>
              <div><Label>Commission Rate (%)</Label><Input type="number" min="0" max="100" value={form.commissionRate} onChange={(event) => setForm({ ...form, commissionRate: event.target.value })} className="mt-1" /></div>
              <div><Label>Availability</Label><Input value={form.availability} onChange={(event) => setForm({ ...form, availability: event.target.value })} className="mt-1" /></div>
              <div><Label>Specialties</Label><Input value={form.specialties} onChange={(event) => setForm({ ...form, specialties: event.target.value })} placeholder="Facials, Lashes" className="mt-1" /></div>
              <div>
                <Label>{editingId ? "New PIN (optional)" : "Staff PIN"}</Label>
                <Input
                  value={form.pin}
                  onChange={(event) => setForm({ ...form, pin: event.target.value.replace(/\D/g, "").slice(0, 4) })}
                  placeholder={editingId ? "Leave blank to keep current PIN" : "4-digit PIN"}
                  className="mt-1"
                  inputMode="numeric"
                  type="password"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button className="w-full" onClick={() => void submit()} disabled={isSaving}>
                {isSaving ? "Saving..." : editingId ? "Save Changes" : "Add Member"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {message && <p className="text-sm text-success">{message}</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {snapshot?.staff.map((member) => (
          <div key={member.id} className="space-y-3">
            <StaffCard {...member} />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditingId(member.id); setOpen(true); }}>
                <Pencil className="mr-1 h-4 w-4" /> Edit
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => { await deleteStaff(member.id); setMessage("Team member deleted."); }}>
                <Trash2 className="mr-1 h-4 w-4" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
